import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useImperativeHandle,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";
import SectionFileSelection from "./SectionFileSelection";
import SectionHeadersDefinition from "./SectionHeadersDefinition";
import SectionVariantSelector from "./SectionVariantSelector";
import {
  Typography,
  Box,
  makeStyles,
  Divider,
  Tooltip,
} from "@material-ui/core";
import {
  sendFilePrepare,
  sendFileUpload,
  getUploadProgress,
  getPrepareProgress,
} from "../../viewServices/apiCalls";
import AuthContext from "connections/auth/AuthProvider.js";
import { toast } from "react-toastify";
import {
  grayColor,
  successColor,
} from "assets/jss/material-dashboard-pro-react.js";
import Button from "components/CustomButtons/Button.js";
import SaveIcon from "@material-ui/icons/Save";
import { useTranslation } from "react-i18next";

const tabViewConfigStyle = (theme, clientColor) => ({
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  contentWrapper: {
    position: "relative",
    height: "100%",
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    marginTop: theme.spacing(3), // Increased top margin for better section separation
    marginBottom: theme.spacing(2),
    color: grayColor[2],
    position: "relative", // Ensure section titles are positioned correctly
    zIndex: 10, // Higher z-index to stay above any overflowing content
  },
  infoBox: {
    marginTop: theme.spacing(2),
    backgroundColor: grayColor[12],
    padding: theme.spacing(2),
    borderRadius: "3px",
    fontSize: "0.875rem",
    color: grayColor[2],
  },
  noInputMessage: {
    fontStyle: "italic",
    color: grayColor[1],
    marginTop: theme.spacing(3),
    textAlign: "center",
  },
  conditionalMessage: {
    fontStyle: "italic",
    color: grayColor[1],
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    textAlign: "left",
    fontSize: "0.875rem",
  },
  saveButton: {
    backgroundColor: clientColor,
    color: "#fff",
    "&:hover": {
      backgroundColor: successColor[1],
    },
  },
});

const ToastNotify = (message, type = "info") =>
  toast(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    theme: "light",
    type: type,
  });

const TabToUpload = React.forwardRef(
  (
    {
      selectedInputTable,
      inputColumns,
      inputFiles,
      ingestionColumns,
      loadColumnsAndFiles,
      onMetadataChange,
      onVariantsRefresh,
      variants,
      selectedVariant,
      readonly,
      onReadonlyChange,
      onFilePreparedChange,
      hideFooter = false,
      onSubmissionStatusChange,
      onUploadStatusChange,
    },
    ref,
  ) => {
    const { userDetails } = useContext(AuthContext);
    const useStyles = makeStyles((theme) =>
      tabViewConfigStyle(theme, userDetails.implementation_color),
    );
    const classes = useStyles();

    const T_PREFIX = "rgm.inputs";
    const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

    const activeTableId = selectedInputTable;
    const activeTable = inputColumns[activeTableId];
    const currentFile = inputFiles[activeTableId];

    // State for file handling
    const [newFile, setNewFile] = useState(null);
    const [fileTabs, setFileTabs] = useState([]);
    const [fileAllHeaders, setFileAllHeaders] = useState({});
    const [fileChecked, setFileChecked] = useState(false);

    // State for header and column mapping
    const [newFileSheet, setNewFileSheet] = useState("");
    const [headerRow, setHeaderRow] = useState(1);
    const [localMappings, setLocalMappings] = useState({});

    // State for variant management
    const [localVariants, setLocalVariants] = useState(variants);
    const [variantSheetMapping, setVariantSheetMapping] = useState({});
    const [variantsToDelete, setVariantsToDelete] = useState([]);

    // Initial state for comparison
    const [initialNewFileSheet, setInitialNewFileSheet] = useState("");
    const [initialHeaderRow, setInitialHeaderRow] = useState(1);
    const [initialLocalMappings, setInitialLocalMappings] = useState({});
    const [initialLocalVariants, setInitialLocalVariants] = useState([]);
    const [initialVariantSheetMapping, setInitialVariantSheetMapping] =
      useState({});

    // Loading states
    const [isPreparingFile, setIsPreparingFile] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [invalidVariants, setInvalidVariants] = useState({});
    const [isBackgroundUploadInProgress, setIsBackgroundUploadInProgress] =
      useState(false);

    // Ref to track polling interval for cleanup
    const pollIntervalRef = useRef(null);
    const isUploadActiveRef = useRef(false);

    const validateVariantMappings = () => {
      const errors = {};
      const assignedSheets = new Set();
      let referenceHeaders = null;
      let hasEmpty = false;
      let hasDuplicates = false;
      let hasInconsistentHeaders = false;

      const activeVariants = localVariants.filter(
        (variant) => !variantsToDelete.includes(variant.variant_id),
      );

      if (!readonly && activeVariants.length === 0) {
        hasEmpty = true;
      }

      // Find the first valid sheet to set the reference headers
      if (fileChecked && Object.keys(fileAllHeaders).length > 0) {
        for (const variant of activeVariants) {
          const sheet = variantSheetMapping[variant.variant_id];
          if (sheet && fileAllHeaders[sheet]) {
            referenceHeaders = JSON.stringify(
              [...fileAllHeaders[sheet]].sort(),
            );
            break;
          }
        }
      }

      activeVariants.forEach((variant) => {
        const sheet = variantSheetMapping[variant.variant_id];
        // Check for empty name for all variants
        if (!variant.variant_name || variant.variant_name.trim() === "") {
          errors[variant.variant_id] = "empty_name";
          hasEmpty = true;
          return; // Skip further checks for this variant if name is empty
        }

        if (!sheet) {
          errors[variant.variant_id] = "empty_sheet";
          hasEmpty = true;
        } else {
          // Only check if sheet exists in fileTabs if file has been checked
          if (fileChecked && !fileTabs.includes(sheet)) {
            errors[variant.variant_id] = "invalid_sheet";
            hasEmpty = true; // Treat as empty for submission prevention
          }

          if (assignedSheets.has(sheet)) {
            errors[variant.variant_id] = "duplicate";
            hasDuplicates = true;
          }
          assignedSheets.add(sheet);

          // Check for header consistency only if a reference is set and file has been checked
          if (fileChecked && referenceHeaders) {
            const currentHeaders = fileAllHeaders[sheet];
            if (
              !currentHeaders ||
              JSON.stringify([...currentHeaders].sort()) !== referenceHeaders
            ) {
              // Only mark as inconsistent if not already marked as duplicate
              if (!errors[variant.variant_id]) {
                errors[variant.variant_id] = "inconsistent";
              }
              hasInconsistentHeaders = true;
            }
          }
        }
      });

      return {
        errors,
        isValid: !hasEmpty && !hasDuplicates,
        hasEmpty,
        hasDuplicates,
        hasInconsistentHeaders,
      };
    };

    useEffect(() => {
      const { errors, isValid } = validateVariantMappings();
      setInvalidVariants(errors);
      console.log("Validation Effect:", {
        errors,
        isValid,
        fileChecked,
        fileTabs,
        fileAllHeaders,
      });
    }, [
      variantSheetMapping,
      localVariants,
      variantsToDelete,
      fileChecked,
      fileTabs,
      fileAllHeaders,
    ]);

    useEffect(() => {
      console.log("--- TabViewConfig useEffect Start ---");
      console.log("Props: ingestionColumns", ingestionColumns);
      console.log("Props: activeTableId", activeTableId);
      console.log("Props: variants", variants);

      setNewFile(null);
      setFileChecked(false);
      setVariantsToDelete([]);

      setLocalVariants(readonly ? variants : []);

      const currentTableIngestionColumns = ingestionColumns[activeTableId];
      console.log(
        "Derived: currentTableIngestionColumns",
        currentTableIngestionColumns,
      );

      const initialVariantSheetMappingTemp = {};
      if (variants && currentTableIngestionColumns) {
        variants.forEach((v) => {
          const variantMappings =
            currentTableIngestionColumns[String(v.variant_id)];
          console.log(
            `Processing variant ${v.variant_id}: variantMappings`,
            variantMappings,
          );
          if (variantMappings) {
            const firstColumn = Object.values(variantMappings)[0];
            if (firstColumn && firstColumn.file_tab_name) {
              initialVariantSheetMappingTemp[v.variant_id] =
                firstColumn.file_tab_name;
            }
          }
        });
      }
      setVariantSheetMapping(initialVariantSheetMappingTemp);
      setInitialVariantSheetMapping(initialVariantSheetMappingTemp);
      console.log(
        "Result: initialVariantSheetMapping",
        initialVariantSheetMappingTemp,
      );

      let initialFileTabs = [];
      let initialFileAllHeaders = {};
      let initialLocalMappingsTemp = {};
      let initialNewFileSheetTemp = "";

      let initialHeaderRowTemp = 1;

      if (currentTableIngestionColumns) {
        const uniqueFileTabs = new Set();
        Object.values(currentTableIngestionColumns).forEach((variantData) => {
          Object.values(variantData).forEach((mapping) => {
            if (mapping.file_tab_name) {
              uniqueFileTabs.add(mapping.file_tab_name);
            }
          });
        });
        initialFileTabs = Array.from(uniqueFileTabs);

        const selectedVariantKey =
          selectedVariant &&
          currentTableIngestionColumns[String(selectedVariant)]
            ? String(selectedVariant)
            : Object.keys(currentTableIngestionColumns)[0];
        if (selectedVariantKey) {
          const selectedVariantMappings =
            currentTableIngestionColumns[selectedVariantKey];
          if (selectedVariantMappings) {
            // Set initialNewFileSheet from the first variant's first column mapping
            const firstMappingInFirstVariant = Object.values(
              selectedVariantMappings,
            )[0];
            if (
              firstMappingInFirstVariant &&
              firstMappingInFirstVariant.file_tab_name
            ) {
              initialNewFileSheetTemp =
                firstMappingInFirstVariant.file_tab_name;
            }

            if (
              firstMappingInFirstVariant &&
              firstMappingInFirstVariant.header_row
            ) {
              initialHeaderRowTemp = firstMappingInFirstVariant.header_row;
            }

            Object.entries(selectedVariantMappings).forEach(
              ([columnId, mapping]) => {
                initialLocalMappingsTemp[columnId] = mapping.file_column_name;
              },
            );

            if (initialNewFileSheetTemp) {
              initialFileAllHeaders[initialNewFileSheetTemp] = Object.values(
                selectedVariantMappings,
              ).map((mapping) => mapping.file_column_name);
            }
          }
        }
      }

      setFileTabs(initialFileTabs);
      setFileAllHeaders(initialFileAllHeaders);
      setLocalMappings(initialLocalMappingsTemp);
      setNewFileSheet(initialNewFileSheetTemp);
      setHeaderRow(initialHeaderRowTemp);

      setInitialNewFileSheet(initialNewFileSheetTemp);
      setInitialHeaderRow(initialHeaderRowTemp);
      setInitialLocalMappings(initialLocalMappingsTemp);
      setInitialLocalVariants(variants);

      console.log("Result: initialFileTabs", initialFileTabs);
      console.log("Result: initialFileAllHeaders", initialFileAllHeaders);
      console.log("Result: initialLocalMappings", initialLocalMappingsTemp);
      console.log("Result: initialNewFileSheet", initialNewFileSheetTemp);
      console.log("--- TabViewConfig useEffect End ---");
    }, [readonly, variants, ingestionColumns, activeTableId, selectedVariant]);

    useEffect(() => {
      if (onFilePreparedChange) {
        onFilePreparedChange(fileChecked);
      }
    }, [fileChecked, onFilePreparedChange]);

    const handleSheetChange = (variantId, sheetName) => {
      setVariantSheetMapping((prev) => ({ ...prev, [variantId]: sheetName }));
    };

    const handleVariantNameChange = (variantId, newName) => {
      setLocalVariants((prev) =>
        prev.map((variant) =>
          variant.variant_id === variantId
            ? { ...variant, variant_name: newName }
            : variant,
        ),
      );
    };

    const handleVariantCreate = (variantName, tempId = null) => {
      const finalTempId = tempId || `temp_${Date.now()}`;
      const newVariant = {
        variant_id: finalTempId,
        variant_name: variantName,
        isNew: true,
      };
      console.log("Creating new variant:", newVariant);
      setLocalVariants((prev) => {
        console.log("Previous variants:", prev);
        const updated = [...prev, newVariant];
        console.log("Updated variants:", updated);
        return updated;
      });
    };

    const handleVariantDelete = (variantId) => {
      const variantToDelete = localVariants.find(
        (v) => v.variant_id === variantId,
      );
      if (variantToDelete) {
        setLocalVariants((prev) =>
          prev.filter((v) => v.variant_id !== variantId),
        );
        setVariantSheetMapping((prev) => {
          const newMapping = { ...prev };
          delete newMapping[variantId];
          return newMapping;
        });
      }
    };

    const handleVariantUndoDelete = (variantId) => {
      setVariantsToDelete((prev) => prev.filter((id) => id !== variantId));
    };

    const getSelectedVariantMappings = () => {
      const tableMappings = ingestionColumns[activeTableId] || {};
      if (selectedVariant && tableMappings[String(selectedVariant)]) {
        return tableMappings[String(selectedVariant)];
      }
      const firstVariantKey = Object.keys(tableMappings)[0];
      return firstVariantKey ? tableMappings[firstVariantKey] : {};
    };

    const buildValidMappingsFromLastChoices = (
      lastMappings,
      availableHeaders,
    ) => {
      const headersSet = new Set(availableHeaders || []);
      return Object.entries(lastMappings || {}).reduce(
        (acc, [columnId, mapping]) => {
          const fileColumnName = mapping?.file_column_name;
          if (
            fileColumnName &&
            fileColumnName !== "N/A" &&
            headersSet.has(fileColumnName)
          ) {
            acc[columnId] = fileColumnName;
          }
          return acc;
        },
        {},
      );
    };

    const handlePrepareFile = async () => {
      console.log("handlePrepareFile called. newFile=", newFile);
      if (!newFile) return;
      setIsPreparingFile(true);
      console.log("setIsPreparingFile(true)");
      try {
        const res = await sendFilePrepare(newFile, activeTableId, headerRow);
        const sheets = res.sheets || {};
        const prepareId = res.prepare_id; // NEW: Get prepare_id from response
        const validSheets = Object.keys(sheets).filter(
          (sheet) => sheets[sheet].length > 0,
        );
        const firstValidSheet = validSheets[0] || "";
        setFileTabs(validSheets);
        setFileAllHeaders(sheets);
        setLocalMappings(
          buildValidMappingsFromLastChoices(
            getSelectedVariantMappings(),
            sheets[firstValidSheet] || [],
          ),
        );
        setNewFileSheet(firstValidSheet);

        // Clear any variant sheet mappings that are no longer valid
        setVariantSheetMapping((prevMapping) => {
          const newMapping = {};
          for (const variantId in prevMapping) {
            if (validSheets.includes(prevMapping[variantId])) {
              newMapping[variantId] = prevMapping[variantId];
            }
          }
          return newMapping;
        });

        setFileChecked(true);
        setIsPreparingFile(false); // Allow user to continue mapping
        if (validSheets.length > 0) {
          const tempId = `temp_${Date.now()}`;
          const fileBaseName = newFile.name.replace(/\.[^/.]+$/, "");
          setLocalVariants([
            {
              variant_id: tempId,
              variant_name: fileBaseName || firstValidSheet,
              isNew: true,
            },
          ]);
          setVariantSheetMapping((prev) => ({
            ...prev,
            [tempId]: firstValidSheet,
          }));
        }
        console.log("setFileChecked(true)");
        ToastNotify(t("toast.headers_read_success"), "success");

        // NEW: Start background upload progress tracking
        if (prepareId) {
          setIsBackgroundUploadInProgress(true);
          const uploadToastId = `prepare-upload-${prepareId}`;
          toast.info(
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid " + userDetails.implementation_color,
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <span>{t("toast.file_preparing")}</span>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>,
            {
              toastId: uploadToastId,
              autoClose: false,
              position: "top-right",
            },
          );
          pollPrepareProgress(prepareId, uploadToastId);
        }
      } catch (err) {
        console.error("Error in handlePrepareFile:", err);
        setIsPreparingFile(false);

        if (err.response?.status === 400 && err.response?.data?.error) {
          const errorData = err.response.data.error;

          if (
            errorData.message &&
            errorData.message.includes("inconsistent headers")
          ) {
            ToastNotify(t("toast.headers_inconsistent"), "error");
          } else {
            const message =
              typeof errorData === "string"
                ? errorData
                : errorData.message || t("toast.file_preparation_failed");
            ToastNotify(message, "error");
          }
        } else {
          ToastNotify(t("toast.headers_read_error"), "error");
        }
      }
    };

    // Cleanup function for polling interval
    const cleanupPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    // NEW: Poll prepare progress for background upload
    const pollPrepareProgress = async (prepareId, toastId) => {
      const pollInterval = setInterval(async () => {
        try {
          const progress = await getPrepareProgress(prepareId);

          if (progress.status === "complete") {
            clearInterval(pollInterval);
            setIsBackgroundUploadInProgress(false);
            toast.update(toastId, {
              render: t("toast.file_prepared"),
              type: "success",
              autoClose: 3000,
            });
          } else if (progress.status === "error") {
            clearInterval(pollInterval);
            setIsBackgroundUploadInProgress(false);
            const errorMessage =
              progress.message_args?.error ||
              progress.message_key ||
              "Unknown error";
            toast.update(toastId, {
              render: t("toast.upload_failed", { error: errorMessage }),
              type: "error",
              autoClose: 5000,
            });
          }
          // else status === 'uploading', keep polling
        } catch (err) {
          console.error("Error polling prepare progress:", err);
          clearInterval(pollInterval);
          setIsBackgroundUploadInProgress(false);
          toast.update(toastId, {
            render: t("toast.file_prepare_error"),
            type: "warning",
            autoClose: 5000,
          });
        }
      }, 2000); // Poll every 2 seconds
    };

    // Cleanup on component unmount - DON'T cleanup polling if upload is active
    // This allows polling to continue even if drawer is closed
    useEffect(() => {
      return () => {
        if (!isUploadActiveRef.current) {
          // Only cleanup if no upload is active
          cleanupPolling();
        } else {
          console.log(
            "Component unmounting but upload is active - keeping polling alive",
          );
        }
      };
    }, []);

    // Simplified progress tracking with race-condition-free polling
    const pollUploadProgress = async (uploadId, existingToastId = null) => {
      const progressToastId = existingToastId || `upload-progress-${uploadId}`;
      let hasCompleted = false;
      let consecutiveErrors = 0;
      let lastProgress = 0;
      let lastMessage = "";
      const MAX_CONSECUTIVE_ERRORS = 5;

      // Mark upload as active
      isUploadActiveRef.current = true;
      if (onUploadStatusChange) {
        onUploadStatusChange(true);
      }

      // Clear any existing polling interval
      cleanupPolling();

      // Create initial toast
      if (!existingToastId) {
        toast.info(
          <ProgressToast
            progress={0}
            message={t("upload.validating")}
            color={userDetails.implementation_color}
          />,
          {
            toastId: progressToastId,
            autoClose: false,
            closeButton: false,
            position: "top-right",
          },
        );
      }

      try {
        pollIntervalRef.current = setInterval(async () => {
          try {
            const backendProgress = await getUploadProgress(uploadId);
            console.log(`Backend progress:`, backendProgress);

            consecutiveErrors = 0;

            // RACE CONDITION FIX: Only stop on confirmed terminal state
            if (
              backendProgress.status === "success" &&
              backendProgress.progress === 100
            ) {
              // Confirmed success at 100%
              hasCompleted = true;
              isUploadActiveRef.current = false;
              if (onUploadStatusChange) {
                onUploadStatusChange(false);
              }
              cleanupPolling();

              const successMessage = backendProgress.message_key
                ? t(backendProgress.message_key)
                : t("upload.complete");

              toast.update(progressToastId, {
                render: (
                  <ProgressToast
                    progress={100}
                    message={successMessage}
                    color="#4caf50"
                  />
                ),
                type: "success",
                autoClose: 5000,
                closeButton: true,
                closeOnClick: true,
              });

              // Reset to readonly mode
              if (onReadonlyChange) {
                onReadonlyChange(true);
              }

              // Clear prepared file state
              setFileChecked(false);
              setNewFile(null);

              // Refresh data
              setTimeout(() => {
                loadColumnsAndFiles();
                if (onMetadataChange) onMetadataChange();
                if (onVariantsRefresh) onVariantsRefresh();
              }, 100);

              setIsProcessing(false);
              return;
            }

            if (backendProgress.status === "error") {
              // Error can happen at any progress level
              hasCompleted = true;
              isUploadActiveRef.current = false;
              if (onUploadStatusChange) {
                onUploadStatusChange(false);
              }
              cleanupPolling();

              const errorMessage =
                backendProgress.error ||
                (backendProgress.message_key
                  ? t(backendProgress.message_key)
                  : "Unknown error");

              toast.update(progressToastId, {
                render: t("toast.upload_failed", { error: errorMessage }),
                type: "error",
                autoClose: false,
                closeButton: true,
                closeOnClick: true,
              });

              setIsProcessing(false);
              return;
            }

            // Update progress (only if changed significantly to reduce noise)
            const progressChanged =
              Math.abs(backendProgress.progress - lastProgress) >= 5;
            const messageChanged = backendProgress.message_key !== lastMessage;

            if (progressChanged || messageChanged) {
              lastProgress = backendProgress.progress;
              lastMessage = backendProgress.message_key;

              const displayMessage = backendProgress.message_key
                ? t(backendProgress.message_key)
                : "Processing...";

              toast.update(progressToastId, {
                render: (
                  <ProgressToast
                    progress={backendProgress.progress}
                    message={displayMessage}
                    color={userDetails.implementation_color}
                  />
                ),
              });
            }
          } catch (error) {
            if (!hasCompleted) {
              consecutiveErrors++;
              console.error(
                `Error polling (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`,
                error,
              );

              if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                hasCompleted = true;
                isUploadActiveRef.current = false;
                if (onUploadStatusChange) {
                  onUploadStatusChange(false);
                }
                cleanupPolling();

                toast.update(progressToastId, {
                  render: t("toast.upload_failed", {
                    error:
                      "Unable to retrieve upload status. Please refresh to verify completion",
                  }),
                  type: "error",
                  autoClose: false,
                  closeButton: true,
                  closeOnClick: true,
                });

                setIsProcessing(false);
              }
            }
          }
        }, 2000);
      } catch (error) {
        console.error("Error setting up progress polling:", error);
        isUploadActiveRef.current = false;
        if (onUploadStatusChange) {
          onUploadStatusChange(false);
        }
        cleanupPolling();
        setIsProcessing(false);
      }
    };

    // Progress toast component
    const ProgressToast = ({ progress, message, color }) => (
      <div>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>{message}</div>
        <div
          style={{
            width: "100%",
            backgroundColor: "#e0e0e0",
            borderRadius: 4,
            height: 6,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              backgroundColor: color,
              height: "100%",
              borderRadius: 4,
              transition: "width 0.5s ease-out",
            }}
          />
        </div>
        <div style={{ fontSize: "0.75em", marginTop: 4, color: "#999" }}>
          {Math.round(progress)}%
        </div>
      </div>
    );

    ProgressToast.propTypes = {
      progress: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    };

    // Main submission handler
    const handleSubmit = async () => {
      // Prevent double submissions
      if (isProcessing) {
        console.log("Submission already in progress, ignoring duplicate call");
        return;
      }

      // Upload now always creates new variants from a newly prepared file.
      if (!newFile || !fileChecked) {
        ToastNotify(t("toast.file_not_prepared"), "warning");
        return;
      }

      const validationResult = validateVariantMappings();
      if (!validationResult.isValid) {
        const errorValues = Object.values(validationResult.errors);
        if (validationResult.hasEmpty) {
          ToastNotify(t("toast.empty_variant_sheet"), "error");
        }
        if (errorValues.includes("empty_name")) {
          ToastNotify(t("toast.empty_variant_name"), "error");
        }
        if (validationResult.hasDuplicates) {
          ToastNotify(t("toast.duplicate_variant_sheet"), "error");
        }
        if (validationResult.hasInconsistentHeaders) {
          ToastNotify(t("toast.inconsistent_headers"), "error");
        }
        if (errorValues.includes("invalid_sheet")) {
          ToastNotify(t("toast.invalid_sheet_selected"), "error");
        }
        return;
      }

      setIsProcessing(true);
      console.log("Starting submission process...");

      const newVariantsToCreate = localVariants
        .filter((v) => v.isNew)
        .map((v) => ({
          tempId: v.variant_id,
          name: v.variant_name,
          sheet: variantSheetMapping[v.variant_id],
        }));

      const newVariantSheetMapping = {};
      newVariantsToCreate.forEach((variant) => {
        newVariantSheetMapping[variant.tempId] = variant.sheet;
      });

      const variantOperations = {
        create: newVariantsToCreate,
        delete: [],
        map: newVariantSheetMapping,
        updateNames: {},
      };

      const fileToProcessName = newFile ? newFile.name : currentFile?.file_name;

      // Show initial progress toast immediately
      const progressToastId = `upload-progress-${Date.now()}`;
      toast.info(
        <div>
          <div style={{ marginBottom: "8px" }}>Starting upload...</div>
          <div
            style={{
              width: "100%",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              height: "8px",
            }}
          >
            <div
              style={{
                width: "0%",
                backgroundColor: userDetails.implementation_color,
                height: "100%",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "0.8em",
              marginTop: "4px",
              color: "#666",
            }}
          >
            0% complete
          </div>
        </div>,
        {
          toastId: progressToastId,
          autoClose: false,
          closeButton: false,
          position: "top-right",
        },
      );

      // Helper function to check if error is retryable (network/connectivity issues)
      const isRetryableError = (error) => {
        if (!error.response) {
          // Network error (no response received)
          return true;
        }

        const status = error.response.status;
        // Retry on server errors (5xx) and some client errors that might be temporary
        return status >= 500 || status === 408 || status === 429;
      };

      // Helper function to delay execution
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      // Upload with retry logic
      const uploadWithRetry = async (uploadData, maxRetries = 3) => {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            return await sendFileUpload(uploadData);
          } catch (error) {
            lastError = error;

            if (isRetryableError(error) && attempt < maxRetries) {
              const delayMs = Math.pow(3, attempt) * 1000; // 3s, 9s, 27s
              console.log(
                `Upload attempt ${attempt} failed, retrying in ${delayMs / 1000}s...`,
              );

              // Update toast to show retry with spinner
              toast.update(progressToastId, {
                render: (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        border: "2px solid #ff9800",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        marginRight: "12px",
                      }}
                    />
                    <span>
                      Network error - retrying in {delayMs / 1000}s... (attempt{" "}
                      {attempt}/{maxRetries})
                    </span>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}</style>
                  </div>
                ),
                type: "warning",
                autoClose: false,
                closeButton: false,
              });

              await delay(delayMs);

              // Reset spinner toast for next attempt
              toast.update(progressToastId, {
                render: (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        border: `2px solid ${userDetails.implementation_color}`,
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        marginRight: "12px",
                      }}
                    />
                    <span>
                      Retrying upload... (attempt {attempt + 1}/{maxRetries})
                    </span>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}</style>
                  </div>
                ),
                type: "info",
                autoClose: false,
                closeButton: false,
              });
            } else {
              // Not retryable or max retries reached
              throw lastError;
            }
          }
        }

        throw lastError;
      };

      try {
        // Start upload and get upload_id immediately
        const response = await uploadWithRetry({
          tableId: activeTableId,
          fileName: fileToProcessName,
          columnMapping: localMappings,
          variantOperations: variantOperations,
          headerRow: headerRow,
        });

        // Check if response contains upload_id for progress tracking
        if (response.upload_id) {
          console.log(
            "Got upload_id, starting progress tracking:",
            response.upload_id,
          );

          // Start progress polling immediately with the upload ID
          setTimeout(() => {
            pollUploadProgress(response.upload_id, progressToastId);
          }, 100);
        } else {
          // Fallback for old response format without upload_id
          toast.dismiss(progressToastId);
          ToastNotify(t("toast.processing_fallback"), "success");

          // Reset to readonly mode after successful submission
          if (onReadonlyChange) {
            onReadonlyChange(true);
          }

          // Clear prepared file state
          setFileChecked(false);
          setNewFile(null);

          // Refresh data from backend
          loadColumnsAndFiles();
          if (onMetadataChange) onMetadataChange();
          if (onVariantsRefresh) onVariantsRefresh();

          setIsProcessing(false);
        }
      } catch (err) {
        console.error("Upload error:", err);

        // Check if error response contains upload_id for progress tracking
        const errorData = err.response?.data;
        if (errorData && errorData.upload_id) {
          console.log(
            "Error response contains upload_id, starting progress tracking:",
            errorData.upload_id,
          );

          // Start progress polling with the error upload ID
          setTimeout(() => {
            pollUploadProgress(errorData.upload_id, progressToastId);
          }, 100);

          return; // Exit early, let progress polling handle the error display
        }

        // Fallback error handling for cases without upload_id
        toast.dismiss(progressToastId);

        // Enhanced error handling with specific messages
        let errorMessage = t("toast.upload_failed", { error: "Unknown error" });
        let showRetryOption = false;

        if (!err.response) {
          // Network error - no response received
          errorMessage = t("toast.network_error");
          showRetryOption = true;
        } else {
          const status = err.response.status;

          if (status >= 500) {
            // Server error
            errorMessage = t("toast.server_error");
            showRetryOption = true;
          } else if (status === 408) {
            // Request timeout
            errorMessage = t("toast.timeout_error");
            showRetryOption = true;
          } else if (status === 429) {
            // Rate limited
            errorMessage = t("toast.rate_limit_error");
            showRetryOption = true;
          } else if (status === 400) {
            // Bad request - likely validation error
            if (errorData && errorData.error) {
              if (typeof errorData.error === "string") {
                errorMessage = t("toast.upload_failed", {
                  error: errorData.error,
                });
              } else if (errorData.error.message) {
                errorMessage = t("toast.upload_failed", {
                  error: errorData.error.message,
                });
              }
            }

            // Check for specific blob-related errors
            if (
              errorData?.error &&
              typeof errorData.error === "string" &&
              (errorData.error.toLowerCase().includes("blob") ||
                errorData.error.toLowerCase().includes("file not found"))
            ) {
              errorMessage = t("toast.file_not_found_error");
              showRetryOption = false; // Need to re-prepare file
            }
          } else if (status === 413) {
            // File too large
            errorMessage = t("toast.file_too_large_error");
          } else {
            // Other client errors
            const fallbackError = errorData?.error || `Error ${status}`;
            errorMessage = t("toast.upload_failed", { error: fallbackError });
          }
        }

        // Show error toast with enhanced message
        ToastNotify(errorMessage, "error");

        // If it's a retryable error, show additional guidance
        if (showRetryOption) {
          setTimeout(() => {
            ToastNotify(t("toast.retry_tip"), "info");
          }, 2000);
        }

        setIsProcessing(false);
      }
    };

    const hasChanges = useMemo(() => {
      if (newFile && !fileChecked) return false;

      if (fileChecked) return true;

      if (newFileSheet !== initialNewFileSheet) return true;

      if (headerRow !== initialHeaderRow) return true;

      if (
        JSON.stringify(localMappings) !== JSON.stringify(initialLocalMappings)
      )
        return true;

      if (variantsToDelete.length > 0) return true; // Any deletion is a change

      if (localVariants.some((v) => v.isNew)) return true;

      if (
        JSON.stringify(variantSheetMapping) !==
        JSON.stringify(initialVariantSheetMapping)
      )
        return true;
      return false;
    }, [
      newFile,
      fileChecked,
      newFileSheet,
      headerRow,
      localMappings,
      localVariants,
      variantSheetMapping,
      variantsToDelete,
      initialNewFileSheet,
      initialHeaderRow,
      initialLocalMappings,
      initialLocalVariants,
      initialVariantSheetMapping,
    ]);

    const validationResult = validateVariantMappings();

    // Check if there are any variants with errors (excluding deleted ones)
    const hasVariantErrors = Object.keys(validationResult.errors).length > 0;

    // Check if file is prepared and ready
    const isFileReady = fileChecked && newFile;

    const isSubmitDisabled =
      !validationResult.isValid ||
      !hasChanges ||
      isProcessing ||
      !isFileReady ||
      hasVariantErrors ||
      isBackgroundUploadInProgress;

    useEffect(() => {
      if (onSubmissionStatusChange) {
        onSubmissionStatusChange(isSubmitDisabled);
      }
    }, [isSubmitDisabled, onSubmissionStatusChange]);

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      isUploadInProgress: () => isUploadActiveRef.current,
    }));

    if (!activeTable || !activeTable.columns) {
      return (
        <Typography className={classes.noInputMessage}>
          Configuration for this input is not available or still loading.
        </Typography>
      );
    }

    return (
      <div className={classes.contentWrapper}>
        {isPreparingFile && (
          <div className={classes.loadingOverlay}>
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color={userDetails.implementation_color}
              ariaLabel="three-dots-loading"
              visible={true}
            />
          </div>
        )}
        <Typography className={classes.sectionTitle}>
          {t("file_selection.title")}
        </Typography>
        <SectionFileSelection
          readonly={readonly}
          newFile={newFile}
          currentFile={currentFile}
          isPreparingFile={isPreparingFile}
          fileChecked={fileChecked}
          headerRow={headerRow}
          currentHeaderRow={headerRow}
          implementationColor={userDetails.implementation_color}
          onFileSelect={(e) => {
            setNewFile(e.target.files?.[0] || null);
            setFileChecked(false);
            setLocalMappings({});
            setLocalVariants([]);
            setVariantSheetMapping({});
          }}
          onFileClear={() => {
            setNewFile(null);
            setFileChecked(false);
            setLocalMappings({});
            setLocalVariants([]);
            setVariantSheetMapping({});
          }}
          onPrepareFile={handlePrepareFile}
          onHeaderRowChange={(value) => {
            if (!fileChecked) {
              setHeaderRow(value);
            }
          }}
          className={classes.fileSelectContainer}
        />
        <Divider sx={{ my: 4 }} />

        <Typography className={classes.sectionTitle}>
          {t("headers_definition.title")}
        </Typography>
        {!readonly && !fileChecked ? (
          <Typography className={classes.conditionalMessage}>
            {t("select_file_and_read_headers_to_continue")}
          </Typography>
        ) : (
          <SectionHeadersDefinition
            readonly={readonly}
            currentTab={newFileSheet}
            currentMappings={localMappings}
            activeColumns={activeTable?.columns || {}}
            fileTabs={fileTabs}
            newFileSheet={newFileSheet}
            newFileHeaders={fileAllHeaders[newFileSheet] || []}
            localMappings={localMappings}
            onChangeTab={(sheetName) => {
              setNewFileSheet(sheetName);
              setLocalMappings(
                buildValidMappingsFromLastChoices(
                  getSelectedVariantMappings(),
                  fileAllHeaders[sheetName] || [],
                ),
              );
            }}
            onMappingChange={(key, value) =>
              setLocalMappings((prev) => ({ ...prev, [key]: value }))
            }
          />
        )}
        <Divider sx={{ my: 4 }} />

        <Typography className={classes.sectionTitle}>
          {t("variant_editing.title")}
        </Typography>
        {!readonly && !fileChecked ? (
          <Typography className={classes.conditionalMessage}>
            {t("select_file_and_read_headers_to_continue")}
          </Typography>
        ) : (
          <Box mb={2}>
            <SectionVariantSelector
              variants={localVariants}
              variantSheetMapping={variantSheetMapping}
              onSheetChange={handleSheetChange}
              onVariantCreate={handleVariantCreate}
              onVariantDelete={handleVariantDelete}
              onVariantUndoDelete={handleVariantUndoDelete}
              onVariantNameChange={handleVariantNameChange}
              variantsToDelete={variantsToDelete}
              readonly={readonly}
              fileTabs={fileTabs}
              invalidVariants={invalidVariants}
            />
          </Box>
        )}
        {!hideFooter && (
          <>
            {!readonly && (
              <Box mt={4} display="flex" justifyContent="center">
                <Tooltip
                  title={
                    isBackgroundUploadInProgress
                      ? "Please wait for file preparation to complete"
                      : !isFileReady
                        ? t("please_select_and_prepare_file_first")
                        : hasVariantErrors
                          ? t("please_fix_variant_errors_first")
                          : !hasChanges
                            ? t("no_changes_to_save")
                            : ""
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      variant="contained"
                      className={classes.saveButton}
                      onClick={handleSubmit}
                      startIcon={<SaveIcon />}
                      disabled={isSubmitDisabled}
                    >
                      {isProcessing ? t("processing") : t("save_and_process")}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
            <Divider sx={{ my: 4 }} />

            <Box className={classes.infoBox}>
              <Typography>{t("no_warning")}</Typography>
            </Box>
          </>
        )}
      </div>
    );
  },
);

TabToUpload.displayName = "TabToUpload";

TabToUpload.propTypes = {
  selectedInputTable: PropTypes.string.isRequired,
  inputColumns: PropTypes.object.isRequired,
  inputFiles: PropTypes.object.isRequired,
  ingestionColumns: PropTypes.object.isRequired,
  loadColumnsAndFiles: PropTypes.func.isRequired,
  onMetadataChange: PropTypes.func,
  onVariantsRefresh: PropTypes.func,
  variants: PropTypes.array.isRequired,
  selectedVariant: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readonly: PropTypes.bool.isRequired,
  onReadonlyChange: PropTypes.func,
  onFilePreparedChange: PropTypes.func,
  hideFooter: PropTypes.bool,
  onSubmissionStatusChange: PropTypes.func,
  onUploadStatusChange: PropTypes.func,
};

export default TabToUpload;
